<template>
  <div class="ai-caption-editor">
    <div class="ai-caption-editor__header">
      <div class="ai-caption-editor__title">
        <IconEmbedded name="ai_2-5" color="brandColor" :size="17" />
        <span>Adjust the Caption with AI</span>
      </div>
      <LinkWithIcon v-if="step === 'suggestion'" color="teal" isNoUnderline :isDisabled="isGenerating" @click="goBackToInput">
        <template #icon><IconEmbedded name="caret-top_2-5" :size="15" /></template>
        <span>Back</span>
      </LinkWithIcon>
      <LinkWithIcon v-else color="teal" isNoUnderline :isDisabled="isGenerating" @click="emit('close')">
        <template #icon><IconEmbedded name="remove_3-5" :size="12" /></template>
        <span>Close</span>
      </LinkWithIcon>
    </div>

    <div v-if="step === 'input'" class="ai-caption-editor__input-step">
      <!--<div class="ai-caption-editor__field-label">Quick actions</div>-->
      <div class="ai-caption-editor__quick-actions">
        <button
          v-for="action in quickActions"
          :key="action.label"
          type="button"
          :class="['ai-caption-editor__quick-action', { 'ai-caption-editor__quick-action--selected': selectedQuickAction === action.label }]"
          :disabled="props.isDisabled || isGenerating"
          @click="selectedQuickAction = selectedQuickAction === action.label ? '' : action.label"
        >
          <IconEmbedded class="ai-caption-editor__quick-action-icon" :name="action.iconName" :size="15" color="rgba(0, 0, 0, 0.68)" />
          <span>{{ action.label }}</span>
        </button>
      </div>

      <label class="ai-caption-editor__field">
        <span class="ai-caption-editor__field-label"
          >What should change?
          <!--<span class="ai-caption-editor__optional-label">(optional)</span>--></span
        >
        <textarea
          class="global-textarea ai-caption-editor__guidance"
          v-model="userInstruction"
          :disabled="props.isDisabled || isGenerating"
          maxlength="1000"
          placeholder="Tell AI what to change..."
        ></textarea>
      </label>

      <div v-if="errorMessage" class="ai-caption-editor__error">{{ errorMessage }}</div>

      <div class="ai-caption-editor__input-actions">
        <SrpButton size="small" color="teal" :isDisabled="props.isDisabled || isGenerating" @click="generateCaption">
          <template #icon>
            <LoadingIcon v-if="isGenerating" :size="17" />
            <IconEmbedded v-else name="ai_2-5" :size="17" />
          </template>
          Generate
        </SrpButton>
      </div>
    </div>

    <div v-else class="ai-caption-editor__suggestion-step">
      <div class="ai-caption-editor__field-label">Generated caption:</div>
      <div class="ai-caption-editor__suggestion-box">
        <div v-if="isGenerating" class="ai-caption-editor__suggestion-loading">
          <LoadingIcon :size="18" />
          <span>Generating...</span>
        </div>
        <textarea v-else class="global-textarea ai-caption-editor__suggestion-textarea" v-model="suggestionCaption" :disabled="props.isDisabled" maxlength="2000"></textarea>
      </div>

      <div v-if="errorMessage" class="ai-caption-editor__error">{{ errorMessage }}</div>

      <div class="ai-caption-editor__suggestion-actions">
        <SrpButton size="small" color="teal" :isDisabled="props.isDisabled || isGenerating || !suggestionCaption" @click="applySuggestion"> Replace Current Caption </SrpButton>

        <LinkWithIcon color="gray" isNoUnderline :isDisabled="isGenerating || !suggestionCaption" @click="copySuggestion">
          <template #icon>
            <IconEmbedded v-if="isCopied" name="check_2-5" :size="16" />
            <IconEmbedded v-else name="copy_2-5" :size="16" />
          </template>
          <span>{{ isCopied ? "Copied" : "Copy" }}</span>
        </LinkWithIcon>

        <LinkWithIcon color="gray" isNoUnderline :isDisabled="props.isDisabled || isGenerating" @click="generateCaption">
          <template #icon>
            <IconEmbedded name="reload_2-5" :size="16" />
          </template>
          <span>Try again</span>
        </LinkWithIcon>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import ShareStopSummaryRepo from "@repos/ShareStopSummaryRepo";
import type { SocialPostCaptionAssistRequest } from "@contracts/shareStop";

// Components
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import SrpButton from "@components/ui/SrpButton.vue";

const quickActions = [
  { label: "Rephrase", iconName: "edit-pencil_2" },
  { label: "Shorten", iconName: "minus_2" },
  { label: "Expand", iconName: "plus_2" },
  { label: "More fun", iconName: "party-popper" },
  { label: "More formal", iconName: "briefcase_2" },
] as const;

const props = withDefaults(
  defineProps<{
    customerId: string;
    calendarId: string;
    postId: string;
    currentCaption: string;
    assetIds: Array<string>;
    isDisabled?: boolean;
  }>(),
  {
    customerId: "",
    calendarId: "",
    postId: "",
    currentCaption: "",
    assetIds: () => [],
    isDisabled: false,
  }
);

const emit = defineEmits<{
  (e: "applyCaption", caption: string): void;
  (e: "close"): void;
}>();

const step = ref<"input" | "suggestion">("input");
const selectedQuickAction = ref<string>("");
const userInstruction = ref<string>("");
const suggestionCaption = ref<string>("");
const isGenerating = ref<boolean>(false);
const isCopied = ref<boolean>(false);
const errorMessage = ref<string>("");

async function generateCaption(): Promise<void> {
  if (props.isDisabled || isGenerating.value) return;
  if (!props.calendarId) {
    errorMessage.value = "Could not generate a caption because this post is missing calendar context.";
    return;
  }

  isGenerating.value = true;
  errorMessage.value = "";
  step.value = "suggestion";

  const payload: SocialPostCaptionAssistRequest = {
    postId: props.postId,
    currentCaption: props.currentCaption ?? "",
    userInstruction: userInstruction.value,
    quickAction: selectedQuickAction.value,
    assetIds: props.assetIds ?? [],
  };

  try {
    const result = await ShareStopSummaryRepo.generateCaptionAssist(props.customerId, props.calendarId, payload);
    suggestionCaption.value = result.caption ?? "";
    if (!suggestionCaption.value) errorMessage.value = "AI did not return a caption. Try adding a little more guidance.";
  } catch (err) {
    console.error("AI caption assist failed", err);
    errorMessage.value = "Could not generate a caption. Please try again.";
  } finally {
    isGenerating.value = false;
  }
}

function applySuggestion(): void {
  if (!suggestionCaption.value || props.isDisabled) return;
  emit("applyCaption", suggestionCaption.value);
}

function goBackToInput(): void {
  if (isGenerating.value) return;
  step.value = "input";
}

function copySuggestion(): void {
  if (!suggestionCaption.value || isGenerating.value) return;
  navigator.clipboard.writeText(suggestionCaption.value);
  isCopied.value = true;
  setTimeout(() => (isCopied.value = false), 3000);
}
</script>

<style scoped lang="scss">
.ai-caption-editor {
  padding: 14px;
  border: 1px rgba(17, 134, 137, 0.35) solid;
  border-radius: 5px;
  background: rgba(17, 134, 137, 0.04);

  &__header {
    margin-bottom: 14px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
  }

  &__title {
    display: flex;
    align-items: center;
    gap: 7px;
    color: #3d5557;
    font: 600 15px/19px sans-serif;
  }

  &__field {
    display: block;
  }

  &__field-label {
    margin-bottom: 7px;
    display: block;
    color: #343434;
    font: 600 13px/17px sans-serif;
  }

  &__optional-label {
    font-weight: 400;
  }

  &__quick-actions {
    margin-bottom: 14px;
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }

  &__quick-action {
    padding: 7px 13px;
    border: 1px rgba(0, 0, 0, 0.13) solid;
    border-radius: 5px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #343434;
    font: 600 13px/17px sans-serif;
    background: #fff;
    cursor: pointer;

    &:disabled {
      opacity: 0.5;
      cursor: default;
    }

    &--selected {
      border-color: rgba(17, 134, 137, 0.65);
      color: #0a6668;
      background: rgba(17, 134, 137, 0.08);
    }
  }

  &__quick-action-icon {
    opacity: 1;
  }

  &__guidance {
    min-height: 45px;
    height: 77px;
    margin-bottom: 10px;
    resize: vertical;
  }

  &__input-actions {
    display: flex;
    justify-content: flex-end;
  }

  &__suggestion-box {
    margin-bottom: 12px;
  }

  &__suggestion-textarea {
    height: 115px;
    resize: vertical;
  }

  &__suggestion-loading {
    padding: 13px;
    border: 1px rgba(0, 0, 0, 0.11) solid;
    border-radius: 5px;
    min-height: 72px;
    display: flex;
    align-items: center;
    gap: 9px;
    color: rgba(0, 0, 0, 0.6);
    font: 14px/18px sans-serif;
  }

  &__suggestion-actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 14px;
  }

  &__error {
    margin: 0 0 10px;
    color: #9f3838;
    font: 600 13px/17px sans-serif;
  }
}
</style>
